import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  CheckSquare, 
  ClipboardList, 
  Brain, 
  UserPlus, 
  Plus,
  ArrowRight,
  X,
  CalendarIcon,
  Users,
  Building,
  Package,
  Settings
} from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTasks } from "@/hooks/useTasks";
import { getGlobalStats, isPendingApproval } from "@/services/obraStats";
import { getObraById } from "@/lib/obraCache";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TaskDetailsModal } from "@/components/tasks2/TaskDetailsModal";
import { Task2, Project } from "@/types/tasks";
import { format, isToday, isPast, startOfDay } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { NovaObraModal } from "@/components/obras/NovaObraModal";
import { PreencherRelatorioModal } from "@/components/rdo/PreencherRelatorioModal";
import { NovaObraData } from "@/hooks/useNovaObra";

const quickActions = [
  {
    title: "Preencher RDO",
    description: "Relatório diário de obra",
    icon: FileText,
    count: null,
    color: "text-primary",
    bgColor: "bg-primary/10",
    configKey: "quickActionsRelatorio"
  },
  {
    title: "Aprovar RDOs",
    description: "Pendentes de aprovação",
    icon: CheckSquare,
    count: null,
    color: "text-warning",
    bgColor: "bg-warning/10",
    configKey: "quickActionsRDO"
  },
  {
    title: "Acessar Tarefas",
    description: "Atrasadas e de hoje",
    icon: ClipboardList,
    count: null,
    color: "text-success",
    bgColor: "bg-success/10",
    configKey: "quickActionsTarefas"
  },
  {
    title: "Sugestões da IA",
    description: "Análises da IA",
    icon: Brain,
    count: null,
    color: "text-purple-600",
    bgColor: "bg-purple-100",
    configKey: "quickActionsSugestoes"
  },
  {
    title: "Cadastros",
    description: "Acessar cadastros",
    icon: UserPlus,
    count: null,
    color: "text-blue-600",
    bgColor: "bg-blue-100",
    configKey: "quickActionsCadastros"
  },
  {
    title: "Nova Obra",
    description: "Criar projeto",
    icon: Plus,
    count: null,
    color: "text-primary",
    bgColor: "bg-primary/10",
    configKey: "quickActionsNovaObra"
  }
];

// Sem sugestões inventadas — as análises da IA virão dos dados reais (Obra IA)
interface AISuggestionItem {
  id: string;
  titulo: string;
  descricao: string;
  categoria: string;
  prioridade: string;
  impacto: string;
  dataGeracao: string;
}
const aiSuggestions: AISuggestionItem[] = [];

// Mock data for cadastros available
const cadastrosDisponiveis = [
  {
    id: "cad-001",
    titulo: "Clientes",
    descricao: "Gerenciar informações de clientes e empresas",
    icone: Users,
    rota: "/cadastro/clientes",
    categoria: "Pessoas"
  },
  {
    id: "cad-002",
    titulo: "Obras",
    descricao: "Cadastrar e gerenciar projetos e obras",
    icone: Building,
    rota: "/cadastro/obras",
    categoria: "Projetos"
  },
  {
    id: "cad-003",
    titulo: "Materiais",
    descricao: "Controlar estoque e materiais de construção",
    icone: Package,
    rota: "/cadastro/materiais",
    categoria: "Estoque"
  },
  // Fornecedores (cad-004) e Setores e Disciplinas (cad-007) saíram na v1 junto
  // com as telas — não existem as tabelas no banco.
  {
    id: "cad-005",
    titulo: "Equipamentos",
    descricao: "Controlar equipamentos e ferramentas",
    icone: Settings,
    rota: "/cadastro/equipamentos",
    categoria: "Recursos"
  },
  {
    id: "cad-006",
    titulo: "Usuários e Permissões",
    descricao: "Gerenciar usuários e níveis de acesso",
    icone: Users,
    // A rota é /cadastro/usuarios; o atalho apontava para
    // /cadastro/usuarios-permissoes, que não existe — o catch-all engolia e
    // jogava o usuário em /cadastro/obras sem avisar.
    rota: "/cadastro/usuarios",
    categoria: "Sistema"
  }
];

// Mock data for RDOs pending approval
// RDOs pendentes reais (derivados no componente via obraStats)
interface RdoPendenteRow {
  id: string;
  obraId: string;
  date: string;
  numeroRdo: string;
  nomeObra: string;
  ultimaAtualizacao: string;
  status: string;
  criadoPor: string;
}

// Mock data for tasks (overdue and today)
// Tarefas e projetos reais vêm do useTasks (derivados no componente)

interface QuickActionsProps {
  config?: {
    quickActionsRelatorio?: boolean;
    quickActionsRDO?: boolean;
    quickActionsTarefas?: boolean;
    quickActionsSugestoes?: boolean;
    quickActionsCadastros?: boolean;
    quickActionsNovaObra?: boolean;
  };
  isVisible?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}

export const QuickActions = ({ config }: QuickActionsProps) => {
  const navigate = useNavigate();
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isRdoDialogOpen, setIsRdoDialogOpen] = useState(false);
  const [isTasksDialogOpen, setIsTasksDialogOpen] = useState(false);
  const [isAiSuggestionsDialogOpen, setIsAiSuggestionsDialogOpen] = useState(false);
  const [isCadastrosDialogOpen, setIsCadastrosDialogOpen] = useState(false);
  const [isNovaObraDialogOpen, setIsNovaObraDialogOpen] = useState(false);
  const [shouldReopenCadastros, setShouldReopenCadastros] = useState(false);
  const [hiddenCards, setHiddenCards] = useState<number[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task2 | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);

  // Tarefas reais (Supabase)
  const { tasks: tasksReais } = useTasks();
  const projectsReais: Project[] = Array.from(
    new Map(
      tasksReais.map((t) => [t.projectId, { id: t.projectId, name: t.projectName, color: "#0071CE" }])
    ).values()
  );

  // RDOs pendentes de aprovação (reais)
  const [rdosPendentes, setRdosPendentes] = useState<RdoPendenteRow[]>([]);
  useEffect(() => {
    let active = true;
    getGlobalStats()
      .then((s) => {
        if (!active) return;
        const statusLabel: Record<string, string> = {
          draft: "Rascunho",
          in_review: "Em Revisão",
          signed: "Assinado",
        };
        setRdosPendentes(
          s.rdos.filter(isPendingApproval).map((rdo) => ({
            id: rdo.id,
            obraId: rdo.obraId,
            date: rdo.date,
            numeroRdo: rdo.date.split("-").reverse().join("/"),
            nomeObra: getObraById(rdo.obraId)?.nomeObra ?? "—",
            ultimaAtualizacao: rdo.updatedAt
              ? new Date(rdo.updatedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
              : "",
            status: statusLabel[rdo.status ?? "draft"] ?? "Aguardando Aprovação",
            criadoPor: rdo.editHistory?.[rdo.editHistory.length - 1]?.user ?? "—",
          }))
        );
      })
      .catch((e) => console.error("QuickActions RDOs pendentes:", e));
    return () => { active = false; };
  }, []);

  const handleActionClick = (actionTitle: string) => {
    if (actionTitle === "Preencher RDO") {
      setIsReportDialogOpen(true);
    } else if (actionTitle === "Aprovar RDOs") {
      setIsRdoDialogOpen(true);
    } else if (actionTitle === "Acessar Tarefas") {
      setIsTasksDialogOpen(true);
    } else if (actionTitle === "Sugestões da IA") {
      setIsAiSuggestionsDialogOpen(true);
    } else if (actionTitle === "Cadastros") {
      setIsCadastrosDialogOpen(true);
    } else if (actionTitle === "Nova Obra") {
      setIsNovaObraDialogOpen(true);
    }
  };

  const handleCloseCard = (index: number) => {
    setHiddenCards(prev => [...prev, index]);
  };

  // Filter tasks for overdue and today (tarefas reais)
  const getTasksForDisplay = () => {
    const open = tasksReais.filter((t) => t.status !== "completed");
    const overdueTasks = open.filter(task =>
      task.endDate && isPast(task.endDate) && !isToday(task.endDate)
    );
    const todayTasks = open.filter(task =>
      task.endDate && isToday(task.endDate)
    );
    return [...overdueTasks, ...todayTasks];
  };

  const handleTaskClick = (task: Task2) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleTaskUpdate = (updatedTask: Task2) => {
    // In a real app, this would update the task in the backend
    console.log("Task updated:", updatedTask);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Atalhos Rápidos</h2>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {quickActions
          .filter((action) => {
            // Se não tem config, mostra todas as ações
            if (!config) return true;
            // Verifica se a ação específica está habilitada
            return config[action.configKey as keyof typeof config] !== false;
          })
          .filter((_, index) => !hiddenCards.includes(index))
          .map((action, originalIndex) => {
            // Get the original index for the handler
            const actualIndex = quickActions.findIndex(a => a === action);
            return (
              <Card 
                key={actualIndex} 
                className="group relative cursor-pointer hover:shadow-card-hover transition-all duration-200 hover:scale-105"
                onClick={() => handleActionClick(action.title)}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseCard(actualIndex);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
                <CardContent className="p-4 text-center space-y-3">
                  <div className={`mx-auto w-12 h-12 rounded-lg ${action.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <action.icon className={`h-6 w-6 ${action.color}`} />
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-sm text-foreground mb-1">
                      {action.title}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {action.description}
                    </p>
                    {(() => {
                      // Contadores reais por card
                      const realCount =
                        action.title === "Aprovar RDOs"
                          ? rdosPendentes.length
                          : action.title === "Acessar Tarefas"
                          ? getTasksForDisplay().length
                          : action.title === "Sugestões da IA"
                          ? aiSuggestions.length
                          : null;
                      return realCount != null && realCount > 0 ? (
                        <div className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full mt-1 bg-destructive">
                          {realCount}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  
                  <ArrowRight className="h-4 w-4 text-muted-foreground mx-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </CardContent>
              </Card>
            );
          })}
      </div>

      {/* Report Modal (compartilhado — lista obras reais do Supabase) */}
      <PreencherRelatorioModal
        open={isReportDialogOpen}
        onOpenChange={setIsReportDialogOpen}
      />

      {/* RDO Approval Dialog */}
      <Dialog open={isRdoDialogOpen} onOpenChange={setIsRdoDialogOpen}>
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>RDOs Pendentes de Aprovação</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número RDO</TableHead>
                    <TableHead>Nome da Obra</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Última Atualização</TableHead>
                    <TableHead>Criado por</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rdosPendentes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        Nenhum RDO pendente de aprovação. 🎉
                      </TableCell>
                    </TableRow>
                  )}
                  {rdosPendentes.map((rdo) => (
                    <TableRow key={rdo.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="font-medium">{rdo.numeroRdo}</TableCell>
                      <TableCell>{rdo.nomeObra}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          rdo.status === "Rascunho"
                            ? "bg-warning/10 text-warning"
                            : rdo.status === "Em Revisão"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}>
                          {rdo.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{rdo.ultimaAtualizacao}</TableCell>
                      <TableCell>{rdo.criadoPor}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setIsRdoDialogOpen(false);
                            navigate(`/rdo?obra=${rdo.obraId}&date=${rdo.date}`);
                          }}
                        >
                          Abrir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setIsRdoDialogOpen(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tasks Dialog */}
      <Dialog open={isTasksDialogOpen} onOpenChange={setIsTasksDialogOpen}>
        <DialogContent className="sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Tarefas Atrasadas e para Hoje</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarefa</TableHead>
                    <TableHead>Projeto</TableHead>
                    <TableHead>Setor</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Data Limite</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Prioridade</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getTasksForDisplay().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                        Nenhuma tarefa atrasada ou para hoje.
                      </TableCell>
                    </TableRow>
                  )}
                  {getTasksForDisplay().map((task) => {
                    const isOverdue = task.endDate && isPast(task.endDate) && !isToday(task.endDate);
                    return (
                      <TableRow 
                        key={task.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleTaskClick(task)}
                      >
                        <TableCell>
                          <div>
                            <div className="font-medium">{task.title}</div>
                            {task.description && (
                              <div className="text-sm text-muted-foreground">{task.description}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{task.projectName}</TableCell>
                        <TableCell className="text-sm">{task.setor}</TableCell>
                        <TableCell className="text-sm">
                          {task.assignees.length > 0 ? task.assignees[0].name : 'Não atribuído'}
                        </TableCell>
                        <TableCell className={`text-sm ${isOverdue ? 'text-destructive font-medium' : ''}`}>
                          {task.endDate ? format(task.endDate, "dd/MM/yyyy") : '-'}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            task.status === 'not_started' ? 'bg-muted text-muted-foreground' :
                            task.status === 'in_progress' ? 'bg-primary/10 text-primary' :
                            task.status === 'waiting' ? 'bg-warning/10 text-warning' :
                            'bg-success/10 text-success'
                          }`}>
                            {task.status === 'not_started' ? 'Não Iniciado' :
                             task.status === 'in_progress' ? 'Em Execução' :
                             task.status === 'waiting' ? 'Aguardando' : 'Concluído'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            task.priority === 'high' ? 'bg-red-100 text-red-700' :
                            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {task.priority === 'high' ? 'Alta' :
                             task.priority === 'medium' ? 'Média' : 'Baixa'}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setIsTasksDialogOpen(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Details Modal */}
      {showTaskModal && selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          projects={projectsReais}
          isOpen={showTaskModal}
          onClose={() => {
            setShowTaskModal(false);
            setSelectedTask(null);
          }}
          onUpdate={handleTaskUpdate}
          isFullscreen={false}
          onToggleFullscreen={() => {}}
        />
      )}

      {/* AI Suggestions Dialog */}
      <Dialog open={isAiSuggestionsDialogOpen} onOpenChange={setIsAiSuggestionsDialogOpen}>
        <DialogContent className="sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Sugestões da IA</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Prioridade</TableHead>
                    <TableHead>Impacto</TableHead>
                    <TableHead>Data de Geração</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aiSuggestions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        As sugestões inteligentes serão geradas a partir dos dados das suas obras. Use o assistente Obra IA para análises sob demanda.
                      </TableCell>
                    </TableRow>
                  )}
                  {aiSuggestions.map((suggestion) => (
                    <TableRow key={suggestion.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="font-medium">{suggestion.titulo}</TableCell>
                      <TableCell className="max-w-xs">
                        <div className="text-sm text-muted-foreground line-clamp-2">
                          {suggestion.descricao}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          suggestion.categoria === "Produtividade" 
                            ? "bg-blue-100 text-blue-700" 
                            : suggestion.categoria === "Logística"
                            ? "bg-orange-100 text-orange-700"
                            : suggestion.categoria === "Segurança"
                            ? "bg-red-100 text-red-700"
                            : suggestion.categoria === "Sustentabilidade"
                            ? "bg-green-100 text-green-700"
                            : "bg-purple-100 text-purple-700"
                        }`}>
                          {suggestion.categoria}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          suggestion.prioridade === "Alta" ? 'bg-red-100 text-red-700' :
                          suggestion.prioridade === "Média" ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {suggestion.prioridade}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm font-medium text-success">
                        {suggestion.impacto}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {suggestion.dataGeracao}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="default">
                            Aplicar
                          </Button>
                          <Button size="sm" variant="outline">
                            Ver Detalhes
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setIsAiSuggestionsDialogOpen(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cadastros Dialog */}
      <Dialog open={isCadastrosDialogOpen} onOpenChange={setIsCadastrosDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Cadastros Disponíveis</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {cadastrosDisponiveis.map((cadastro) => (
                <Card 
                  key={cadastro.id}
                  className="group cursor-pointer hover:shadow-md transition-all duration-200 hover:scale-105"
                  onClick={() => {
                    if (cadastro.titulo === "Obras") {
                      // Open Nova Obra modal and mark to reopen cadastros later
                      setShouldReopenCadastros(true);
                      setIsCadastrosDialogOpen(false);
                      setIsNovaObraDialogOpen(true);
                    } else {
                      // Navigate to the cadastro route
                      window.location.href = cadastro.rota;
                    }
                  }}
                >
                  <CardContent className="p-2 text-center space-y-1">
                    <div className="mx-auto w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <cadastro.icone className="h-4 w-4 text-primary" />
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-sm text-foreground">
                        {cadastro.titulo}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {cadastro.descricao}
                      </p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                        {cadastro.categoria}
                      </span>
                    </div>
                    
                    <ArrowRight className="h-4 w-4 text-muted-foreground mx-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="flex justify-end space-x-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setIsCadastrosDialogOpen(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nova Obra Modal */}
      <NovaObraModal 
        open={isNovaObraDialogOpen} 
        onOpenChange={(open) => {
          setIsNovaObraDialogOpen(open);
          // If closing Nova Obra modal and should reopen cadastros
          if (!open && shouldReopenCadastros) {
            setShouldReopenCadastros(false);
            setIsCadastrosDialogOpen(true);
          }
        }}
        onSubmit={(data: NovaObraData) => {
          console.log("Nova obra criada:", data);
          // Handle submission logic here
        }}
      />
    </div>
  );
};